/**
 * Domain vocabulary.
 *
 * Every value here was observed in the imported historical data for Mistral Ventilation,
 * Mistral SAV and Hydrokleen Laval. Counts in comments are totals across all three tenants,
 * so the reason a value exists is traceable to real usage rather than guesswork.
 */

// ---------------------------------------------------------------------------
// Pipeline stages
// ---------------------------------------------------------------------------

/**
 * Each tenant defines its own French-language pipeline stages, so the stage list is data
 * (a table), not a union type. `StageKind` is the tenant-independent meaning we attach to
 * each stage so that reporting, colours and "is this job still open?" work everywhere
 * without matching on French strings.
 */
export const STAGE_KINDS = ['intake', 'quoting', 'scheduled', 'in_progress', 'awaiting_payment', 'won', 'lost', 'cancelled'] as const;
export type StageKind = (typeof STAGE_KINDS)[number];

/** Stages whose jobs still need someone to act. Drives the default "open work" filters. */
export const OPEN_STAGE_KINDS: readonly StageKind[] = ['intake', 'quoting', 'scheduled', 'in_progress', 'awaiting_payment'];

/** Stages that end the job's life, one way or another. */
export const TERMINAL_STAGE_KINDS: readonly StageKind[] = ['won', 'lost', 'cancelled'];

// ---------------------------------------------------------------------------
// Where work comes from
// ---------------------------------------------------------------------------

/**
 * How a job arrived. Observed: app (2595), web (987), api (330).
 *
 * `web` is the one that pays for itself: the website form carries full campaign
 * attribution, so this is the join between advertising spend and revenue.
 */
export const JOB_SOURCES = ['app', 'web', 'api', 'phone', 'walk_in', 'import'] as const;
export type JobSource = (typeof JOB_SOURCES)[number];

// ---------------------------------------------------------------------------
// Appointments
// ---------------------------------------------------------------------------

/**
 * A visit's lifecycle.
 *
 * There is no separate "lead" concept: what the previous system called a lead was a booked
 * visit, numbered `{jobNumber}-{sequence}`, and its new/qualified/completed values were the
 * life of that visit. A job in an early stage *is* the enquiry.
 */
export const APPOINTMENT_STATUSES = [
  'scheduled',
  'confirmed',
  'in_progress',
  'completed',
  'cancelled',
  'no_show',
] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

/**
 * How precisely a visit is committed.
 *
 * 1726 historical bookings are an exact slot; 539 are a day plus a period. The business
 * told those customers "mercredi AM", not 08:00 — so rendering them as a precise time would
 * misstate the promise and make the technician late by definition.
 */
export const APPOINTMENT_PRECISIONS = ['exact', 'period', 'day'] as const;
export type AppointmentPrecision = (typeof APPOINTMENT_PRECISIONS)[number];

/** Which part of the day was promised, when the precision is `period`. */
export const APPOINTMENT_PERIODS = ['am', 'pm', 'day', 'evening', 'week'] as const;
export type AppointmentPeriod = (typeof APPOINTMENT_PERIODS)[number];

/** Visits still expected to happen. Drives "what is coming up" everywhere. */
export const UPCOMING_APPOINTMENT_STATUSES: readonly AppointmentStatus[] = ['scheduled', 'confirmed', 'in_progress'];

// ---------------------------------------------------------------------------
// Quotes
// ---------------------------------------------------------------------------

/** Observed: accepted (1180), declined (925), pending (547), draft (84). */
export const QUOTE_STATUSES = ['draft', 'pending', 'accepted', 'declined', 'expired'] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

/** Observed: paid (1892), draft (225), awaitingPayment (93), voided (92), partial (2). */
export const INVOICE_STATUSES = ['draft', 'awaiting_payment', 'partial', 'paid', 'voided'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

/** Observed: creditCard (998), eTransfer (165), check (138), other (73), cash (61), interac (53), ach (9). */
export const PAYMENT_METHODS = ['credit_card', 'e_transfer', 'check', 'cash', 'interac', 'ach', 'other'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/**
 * How the payment was taken. Observed: online (503), cardPresent (511), manual (483).
 * `card_present` is the technician tapping a card in the field, which is why the field
 * app has to be able to collect money.
 */
export const PAYMENT_CHANNELS = ['online', 'card_present', 'manual'] as const;
export type PaymentChannel = (typeof PAYMENT_CHANNELS)[number];

export const PAYMENT_STATUSES = ['pending', 'completed', 'voided', 'refunded'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

/** Observed: completed (3899), noAnswer (25), initialized (1). */
export const CALL_STATUSES = ['ringing', 'in_progress', 'completed', 'no_answer', 'busy', 'failed'] as const;
export type CallStatus = (typeof CALL_STATUSES)[number];

/** Observed: externalFlow (3653) inbound-routed, outgoingCall (272). */
export const CALL_DIRECTIONS = ['inbound', 'outbound'] as const;
export type CallDirection = (typeof CALL_DIRECTIONS)[number];

// ---------------------------------------------------------------------------
// Messaging
// ---------------------------------------------------------------------------

/**
 * A thread is either about a customer relationship or about one job.
 */
export const THREAD_KINDS = ['customer', 'job', 'internal'] as const;
export type ThreadKind = (typeof THREAD_KINDS)[number];

/** How a message reached the customer, or how they reached us. Unified into one thread. */
export const MESSAGE_TRANSPORTS = ['sms', 'email', 'in_app', 'note', 'call'] as const;
export type MessageTransport = (typeof MESSAGE_TRANSPORTS)[number];

export const MESSAGE_AUTHOR_KINDS = ['staff', 'customer', 'system'] as const;
export type MessageAuthorKind = (typeof MESSAGE_AUTHOR_KINDS)[number];

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

/** Observed: inventory (204), service (94), bundle (7). */
export const PRODUCT_KINDS = ['service', 'inventory', 'bundle'] as const;
export type ProductKind = (typeof PRODUCT_KINDS)[number];

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

/**
 * Role decides which application you land in: the two technician roles go to the field app,
 * everyone else to the portal.
 *
 * `lead_technician` exists because the business already draws this line, even though its
 * previous tool had no name for it. Under a two-value role flag, four of Hydrokleen's seven
 * field staff carried per-user grants for quotes, invoices, routes and the timesheets tab
 * while the other three carried none — a clean split with nothing in between, and it lands on
 * the three highest-volume technicians. That is a senior field role, and giving it a name
 * turns twelve bespoke permission sets back into something anyone can read.
 */
export const ROLES = [
  'owner',
  'admin',
  'dispatcher',
  'accountant',
  'lead_technician',
  'technician',
] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LANDING: Record<Role, 'portal' | 'field'> = {
  owner: 'portal',
  admin: 'portal',
  dispatcher: 'portal',
  accountant: 'portal',
  lead_technician: 'field',
  technician: 'field',
};

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

/** The card types the global search can return. Order is the display order. */
export const SEARCH_KINDS = ['customer', 'job', 'quote', 'invoice', 'call', 'message'] as const;
export type SearchKind = (typeof SEARCH_KINDS)[number];

// ---------------------------------------------------------------------------
// Locales
// ---------------------------------------------------------------------------

/** French first: every pipeline stage and service name in the source data is French. */
export const LOCALES = ['fr', 'en'] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'fr';

// ---------------------------------------------------------------------------
// Taxes (Quebec)
// ---------------------------------------------------------------------------

/**
 * Observed on every line item: TPS at 5% and TVQ. Rates live in the database per tenant
 * because they change by statute; these are the values in the exported data and the
 * defaults for a new tenant.
 */
export const DEFAULT_TAXES = [
  { code: 'TPS', rate: '5.000', order: 1 },
  { code: 'TVQ', rate: '9.975', order: 2 },
] as const;
